import React, { useState } from 'react';
import { signIn, forgotPassword, confirmForgotPassword } from '../auth';

export default function Login({ onLogin }) {
  const [view, setView] = useState('login'); // 'login' | 'new_password' | 'forgot_request' | 'forgot_confirm'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [completeChallenge, setCompleteChallenge] = useState(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await signIn(email, password);
      if (result.status === 'NEW_PASSWORD_REQUIRED') {
        setCompleteChallenge(() => result.completeChallenge);
        setView('new_password');
      } else {
        onLogin();
      }
    } catch (err) {
      setError(err.message || 'Login failed. Check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await completeChallenge(newPassword);
      onLogin();
    } catch (err) {
      setError(err.message || 'Failed to set new password.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotRequest = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await forgotPassword(email);
      setInfo(`A reset code was sent to ${email}.`);
      setView('forgot_confirm');
    } catch (err) {
      setError(err.message || 'Failed to send reset code.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotConfirm = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await confirmForgotPassword(email, resetCode, newPassword);
      setInfo('Password reset. Please sign in.');
      setView('login');
      setNewPassword('');
      setConfirmPassword('');
      setResetCode('');
    } catch (err) {
      setError(err.message || 'Failed to reset password. Check the code and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (view === 'new_password') {
    return (
      <div className="login-wrap">
        <div className="login-card">
          <h1>Click Pre-Flight Inspection</h1>
          <p className="login-subtitle">Set a new password to continue.</p>
          <form onSubmit={handleNewPassword}>
            <div className="form-group">
              <label>New Password</label>
              <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required autoFocus />
            </div>
            <div className="form-group">
              <label>Confirm Password</label>
              <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
            </div>
            {error && <p className="login-error">{error}</p>}
            <button type="submit" disabled={loading} className="btn-full">
              {loading ? 'Saving...' : 'Set Password'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (view === 'forgot_request') {
    return (
      <div className="login-wrap">
        <div className="login-card">
          <h1>Click Pre-Flight Inspection</h1>
          <p className="login-subtitle">Enter your email to receive a reset code.</p>
          <form onSubmit={handleForgotRequest}>
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
            </div>
            {error && <p className="login-error">{error}</p>}
            <button type="submit" disabled={loading} className="btn-full">
              {loading ? 'Sending...' : 'Send Reset Code'}
            </button>
            <button type="button" onClick={() => { setError(''); setView('login'); }}
              className="btn-ghost btn-full" style={{ marginTop: 8, textAlign: 'center' }}>
              Back to sign in
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (view === 'forgot_confirm') {
    return (
      <div className="login-wrap">
        <div className="login-card">
          <h1>Click Pre-Flight Inspection</h1>
          {info && <p className="login-info">{info}</p>}
          <form onSubmit={handleForgotConfirm}>
            <div className="form-group">
              <label>Reset Code</label>
              <input type="text" value={resetCode} onChange={(e) => setResetCode(e.target.value)} required autoFocus />
            </div>
            <div className="form-group">
              <label>New Password</label>
              <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Confirm Password</label>
              <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
            </div>
            {error && <p className="login-error">{error}</p>}
            <button type="submit" disabled={loading} className="btn-full">
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
            <button type="button" onClick={() => { setError(''); setView('forgot_request'); }}
              className="btn-ghost btn-full" style={{ marginTop: 8, textAlign: 'center' }}>
              Resend code
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="login-wrap">
      <div className="login-card">
        <h1>Click Pre-Flight Inspection</h1>
        <p className="login-subtitle">Showing the potential impact of a Click deployment</p>
        {info && <p className="login-info">{info}</p>}
        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          {error && <p className="login-error">{error}</p>}
          <button type="submit" disabled={loading} className="btn-full">
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
          <button type="button" onClick={() => { setError(''); setInfo(''); setView('forgot_request'); }}
            className="btn-ghost btn-full" style={{ marginTop: 8, textAlign: 'center' }}>
            Forgot password?
          </button>
        </form>
      </div>
    </div>
  );
}
